import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { Grid } from '@material-ui/core'
import { useHistory } from 'react-router-dom'

import Autocomplete from 'shared/components/Autocomplete'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import SnackBar from 'shared/components/SnackBar'
import { colors } from 'shared/styles/colors'
import ICaminhao from 'modules/caminhao/domain/entities/Caminhao'
import { IFindCaminhaoByPlaca } from 'modules/caminhao/domain/usecases/FindCaminhaoByPlaca'
import { ICancelaViagem } from 'modules/lotes/domain/usecases/CancelaViagem'
import { IFinalizaViagem } from 'modules/lotes/domain/usecases/FinalizaViagem'
import { IValidateCaminhao } from 'modules/lotes/domain/usecases/ValidateCaminhao'
import { useDirecionaCarga } from 'modules/lotes/hooks/direcionaCarga'
import { useFrotista } from 'modules/user/hooks/useFrotista'
import EStatusConta from 'modules/lotes/domain/entities/EStatusConta'
import {
  TipoCarreta,
  TipoCavalo,
} from 'modules/lotes/domain/entities/FindByIdResponse'

import ModalFinalizarCancelarViagem from '../components/ModalFinalizarCancelarViagem'
import LabelValue from '../components/SimpleLabelValue'
import {
  Container,
  ContainerAutocomplete,
  ContainerHelperMessage,
  ErrorMessage,
} from './styles'
import useDebounce from 'shared/hooks/useDebounce'

type TProps = {
  findByPlaca: IFindCaminhaoByPlaca
  validateCaminhao: IValidateCaminhao
  finalizaViagem: IFinalizaViagem
  cancelaViagem: ICancelaViagem
}

export enum EStatusCaminhao {
  EMBARCADO = 'CAMINHAO_EMBARCADO',
  EM_TRANSITO = 'CAMINHAO_EM_TRANSITO',
}

enum StepStatusConta {
  Aprovada = 0,
  EmAnalise = 1,
  Reprovada = 2,
  SemConta = 3,
  ContaNaoNecessaria = 4,
}

const SelecionaCaminhao: React.FC<TProps> = ({
  findByPlaca,
  validateCaminhao,
  finalizaViagem,
  cancelaViagem,
}) => {
  const history = useHistory()
  const { frotistaId } = useFrotista()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState('')
  const [loading, setLoading] = useState(false)
  const [caminhoes, setCaminhoes] = useState<ICaminhao[]>([])
  const [caminhaoInput, setCaminhaoInput] = useState('')
  const [loadingBackdrop, setLoadingBackdrop] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [statusCaminhao, setStatusCaminhao] = useState<EStatusCaminhao>()
  const [contatoFilial, setContatoFilial] = useState('')
  const [loteConjuntoId, setLoteConjuntoId] = useState(0)
  const [statusConta, setStatusConta] = useState<StepStatusConta>(
    StepStatusConta.Aprovada,
  )
  const [openAutocomplete, setOpenAutocomplete] = useState(false)

  const debouncedCaminhao = useDebounce(caminhaoInput, 1000)

  const { caminhao, setCaminhao, lote, setContaPendencias } =
    useDirecionaCarga()

  const getTiposCarretaPermitidos = () => {
    const tiposCarretaPermitidos = []
    lote.tipoCarretaPermitidos.forEach((tipoCarreta: TipoCarreta) => {
      tiposCarretaPermitidos.push(tipoCarreta?.IdTipoCarreta)
    })
    return tiposCarretaPermitidos
  }

  const getTiposCavalosPermitidos = () => {
    const tiposCavalosPermitidos = []
    lote.tipoCavaloPermitidos.forEach((tipoCavalo: TipoCavalo) => {
      tiposCavalosPermitidos.push(tipoCavalo?.IdTipoCavalo)
    })
    return tiposCavalosPermitidos
  }

  useEffect(() => {
    const init = async () => {
      if (debouncedCaminhao && caminhaoInput) {
        setLoading(true)
        const tiposCarretaPermitidos = getTiposCarretaPermitidos()
        const tiposCavalosPermitidos = getTiposCavalosPermitidos()
        try {
          const response = await findByPlaca.execute({
            page: 1,
            per_page: 999,
            placa: caminhaoInput,
            tiposCarretaPermitidos: tiposCarretaPermitidos,
            tiposCavaloPermitidos: tiposCavalosPermitidos,
            ativo: true,
            frotista_id: frotistaId,
          })
          setCaminhoes(response.data)
          setLoading(false)
        } catch (error) {
          setLoading(false)
          setOpen(true)
          setMessages(
            'Problemas ao realizar a consulta, tente novamente mais tarde',
          )
        }
      } else {
        setCaminhoes([])
      }
    }
    init()
  }, [debouncedCaminhao, findByPlaca, caminhaoInput])

  const handleCaminhao = (caminhao: string) => {
    if (caminhao) {
      setOpenAutocomplete(true)
    } else {
      setOpenAutocomplete(false)
    }

    setCaminhaoInput(caminhao)
  }

  const caminhaoVinculadoEmViagem = (messages: string[]) => {
    return messages.find(
      msg =>
        msg.toLowerCase().includes('transito') ||
        msg.toLowerCase().includes('na fila') ||
        msg.toLowerCase().includes('confirmado') ||
        msg.toLowerCase().includes('carregando') ||
        msg.toLowerCase().includes('aguardando aprovacao'),
    )
  }

  const hasContaPendencias = (status?: EStatusConta): boolean => {
    switch (status) {
      case EStatusConta.CONTA_NAO_NECESSARIA:
        setStatusConta(StepStatusConta.ContaNaoNecessaria)
        setContaPendencias(false)
        return false
      case EStatusConta.EM_ANALISE:
        setStatusConta(StepStatusConta.EmAnalise)
        setContaPendencias(true)
        return true
      case EStatusConta.RECUSADA:
        setStatusConta(StepStatusConta.Reprovada)
        setContaPendencias(true)
        return true
      case null:
        setStatusConta(StepStatusConta.SemConta)
        setContaPendencias(true)
        return true
      default:
        setStatusConta(StepStatusConta.Aprovada)
        setContaPendencias(false)
        return false
    }
  }

  const validateCaminhaoPlaca = (newValue: ICaminhao) => {
    if (!newValue) return
    setOpenAutocomplete(false)
    const init = async () => {
      setCaminhao(newValue)
      const params = {
        placa: newValue.veiculoCavalo?.placa,
      }
      const hasPendencias = hasContaPendencias(
        newValue.veiculoCavalo?.frotista?.status_conta,
      )
      if (!hasPendencias) {
        setLoadingBackdrop(true)
        try {
          const response = await validateCaminhao.execute(params)

          setContatoFilial(response.contatoFilial)
          setLoadingBackdrop(false)
          if (response.isValid) {
            setCaminhao(newValue)
            return
          }
          if (
            response.messages.find(msg =>
              msg.toLowerCase().includes('embarcado'),
            )
          ) {
            setStatusCaminhao(EStatusCaminhao.EMBARCADO)
            setLoteConjuntoId(response.loteConjuntoId)
            setOpenModal(true)
          }
          if (caminhaoVinculadoEmViagem(response.messages)) {
            setStatusCaminhao(EStatusCaminhao.EM_TRANSITO)
            setLoteConjuntoId(response.loteConjuntoId)
            setOpenModal(true)
          }
        } catch (error) {
          setLoadingBackdrop(false)
          setOpen(true)
          setMessages(
            'Problemas ao realizar a consulta, tente novamente mais tarde',
          )
        }
      }
    }
    init()
  }

  useEffect(() => {
    if (caminhao === null) {
      setCaminhaoInput('')
    }
  }, [caminhao])

  const EmAnalise = () => {
    return (
      <ErrorMessage>
        A conta de pagamentos vinculada a esse caminhão está em análise. Tente
        selecionar outro caminhão.
      </ErrorMessage>
    )
  }
  const Recusada = () => {
    return (
      <ErrorMessage>
        Caminhão vinculado a um CNPJ sem conta de pagamentos. Tente selecionar
        outro caminhão.
      </ErrorMessage>
    )
  }

  const handleSemConta = () => {
    history.push('/tmov-pay-instructions')
  }
  const SemConta = () => {
    return (
      <ContainerHelperMessage>
        <ErrorMessage>
          Caminhão vinculado a um CNPJ sem conta de pagamentos.
        </ErrorMessage>
        <div
          style={{
            color: colors.red,
            cursor: 'pointer',
            fontWeight: 700,
            zIndex: 1000,
          }}
          onClick={handleSemConta}
        >
          Criar conta.
        </div>
      </ContainerHelperMessage>
    )
  }

  const componentStatusConta = useMemo(() => {
    return [
      {
        component: <></>,
      },
      {
        component: <EmAnalise />,
      },
      {
        component: <Recusada />,
      },
      {
        component: <SemConta />,
      },
      {
        component: <></>,
      },
    ]
  }, [])

  return (
    <Container>
      <Grid container spacing={6} style={{ paddingLeft: 20, paddingRight: 20 }}>
        <Grid container>
          <Grid item xs={12} md={12}>
            <ContainerAutocomplete>
              <Autocomplete
                label="Buscar caminhão"
                placeholder="Digite placa para buscar"
                helperMessage="(apenas veículos permitidos)"
                options={caminhoes}
                inputValue={caminhaoInput}
                onInputChange={(_event, newInputValue) => {
                  handleCaminhao(newInputValue)
                }}
                onChange={(
                  _event: ChangeEvent<HTMLInputElement>,
                  newValue: ICaminhao,
                ) => {
                  validateCaminhaoPlaca(newValue)
                }}
                getOptionLabel={option =>
                  (option as ICaminhao).veiculoCavalo.placa +
                  ' ' +
                  (option as ICaminhao).descricao
                }
                loading={loading}
                open={openAutocomplete}
              />
              {componentStatusConta[statusConta].component}
            </ContainerAutocomplete>
          </Grid>
        </Grid>

        {!caminhao?.descricao ? (
          <></>
        ) : (
          <>
            <Grid
              container
              spacing={6}
              style={{ paddingBottom: 20, paddingTop: 50 }}
            >
              <Grid item md={4}>
                <Grid container>
                  <LabelValue title="Nome" value={caminhao?.descricao} />
                </Grid>
              </Grid>

              <Grid item md={4}>
                <Grid container>
                  <LabelValue
                    title="Placa cavalo"
                    value={caminhao?.veiculoCavalo?.placa}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>

      <SnackBar
        open={open}
        message={messages}
        setOpen={setOpen}
        severity={'error'}
      />

      {loadingBackdrop ? (
        <LoadingBackdrop open={loadingBackdrop} color={colors.green} />
      ) : (
        <></>
      )}

      {openModal ? (
        <ModalFinalizarCancelarViagem
          loteConjuntoId={loteConjuntoId}
          finalizaViagem={finalizaViagem}
          cancelaViagem={cancelaViagem}
          status={statusCaminhao}
          contatoFilial={contatoFilial}
          openModal={openModal}
          setOpenModal={() => setOpenModal(false)}
        />
      ) : (
        <></>
      )}
    </Container>
  )
}

export default SelecionaCaminhao
